/**
 * ============================================================================
 * useDashboardQuery — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook backing the Quality Metrics Dashboard. The
 *          server returns a view-scoped snapshot of all 12 quality metrics +
 *          the four hero stats. The hook polls every 60 s — quality metrics
 *          are recomputed on a 1-minute cadence by metrics-svc, so a tighter
 *          loop would mostly hit the cache.
 * Endpoint: GET /api/v1/quality-metrics?view={view}
 *
 * NOTE on payload shape: the server returns a JSON Map<string, unknown> under
 *   `metric.payload` (drill-down extras vary per metric). We type that field
 *   as `Record<string, unknown>` rather than `any` so call sites either index
 *   safely or narrow explicitly.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type DashboardView =
  | 'QA_DIRECTOR'
  | 'SITE_QUALITY_HEAD'
  | 'DEPARTMENT_MANAGER'
  | 'INSPECTOR';

export type TrafficLightBand = 'GREEN' | 'AMBER' | 'RED' | 'UNKNOWN';

export type MetricKey =
  | 'OPEN_DEVIATIONS'
  | 'OPEN_CAPAS'
  | 'DOC_OVERDUE_PCT'
  | 'TRAINING_CURRENCY_PCT'
  | 'BATCH_RECORD_RIGHT_FIRST_TIME_PCT'
  | 'CHANGE_CONTROL_AGING_DAYS'
  | 'AUDIT_FINDINGS_OPEN'
  | 'COMPLAINT_BACKLOG'
  | 'OOS_OOT_RATE_PCT'
  | 'SUPPLIER_QUALITY_INDEX'
  | 'INSPECTION_READINESS_PCT'
  | 'DEVIATION_AGING_DAYS';

export interface MetricTrendPoint {
  /** ISO-8601 UTC instant for the bucket */
  ts: string;
  /** Bucket value (scaled per metric_kind: count, percentage 0-100, days) */
  value: number;
}

export interface QualityMetric {
  metric_key: MetricKey;
  /** Localized human label cached server-side — fall back to t() if missing */
  label: string;
  /** Display value; null when the metric could not be computed */
  value: number | null;
  /** "COUNT" | "PERCENTAGE" | "DAYS" — drives formatting */
  unit_kind: 'COUNT' | 'PERCENTAGE' | 'DAYS';
  band: TrafficLightBand;
  trend: MetricTrendPoint[];
  computed_at_utc: string;
  /** seconds after computed_at_utc beyond which the value is considered stale */
  stale_seconds: number;
  /** Arbitrary per-metric extras (e.g., site_code, top contributing area). */
  payload: Record<string, unknown>;
}

export interface DashboardHeroStat {
  key: 'OPEN_DEVIATIONS' | 'OPEN_CAPAS' | 'DOC_OVERDUE' | 'TRAINING_CURRENCY';
  label: string;
  value: number;
  delta_vs_yesterday: number | null;
  band: TrafficLightBand;
}

export interface DashboardResponse {
  view: DashboardView;
  hero_stats: DashboardHeroStat[];
  metrics: QualityMetric[];
  /** Server-computed wall-clock instant — used to compute stale-data badges. */
  generated_at_utc: string;
}

export function useDashboardQuery(view: DashboardView): UseQueryResult<DashboardResponse> {
  return useQuery<DashboardResponse>({
    queryKey: ['quality-metrics', 'dashboard', view],
    queryFn: ({ signal }) =>
      apiFetch<DashboardResponse>('/api/v1/quality-metrics', {
        query: { view },
        signal,
      }),
    // metrics-svc recomputes on a 1-minute cadence; refetch at the same rate
    // so the dashboard tracks fresh data without thundering the API.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
